// Hardcoded pricing-rule payloads streamed to the customizer in discovery mode.
//
// Mirrors discoveryPrintMethods.ts: the backoffice forces a sample/sandbox pricing rule onto the previewed
// product over postMessage (DiscoveryModeUpdate, category 'pricing'). The customizer's discovery pricing
// simulator (test mode only) computes a delta from the live design so the footer price reacts to the rule —
// all simulated, and kept separate from the product's real pricing engine.
//
// Each rule mirrors — with the same sample thresholds — one rule from the backoffice pricing-rule setup page
// (pricingsAdvanced), so the demo behaves like the real thing. These payloads are a small DSL consumed by the
// customizer's `discoveryPricing.ts` simulator (NOT the full production Pricing schema). Each rule is a
// `{ kind, ... }` shape; a `bracket` is `{ max, price }` where `max: null` is the open-ended top tier:
//   - { kind: 'per-element', tiers: bracket[] }            -> flat fee bracketed by element count
//   - { kind: 'per-side', tiers: bracket[] }               -> flat fee bracketed by customized-side count
//   - { kind: 'per-color', tiers: bracket[] }              -> flat fee bracketed by distinct-color count
//   - { kind: 'quantity-tiers', tiers: [{minQuantity, price}] } -> per-element fee by order quantity
//   - { kind: 'per-text-element', price }                  -> price per text element
//   - { kind: 'per-image-upload', price }                  -> price per image element
//   - { kind: 'element-size', tiers: bracket[] }           -> per-element fee bracketed by element area (cm²)
//   - { kind: 'canvas-size', unitCm2, pricePerUnit }       -> incremental fee per unit of canvas area
//   - { kind: 'design-size', tiers: bracket[] }            -> flat fee bracketed by total design area (cm²)
//   - { kind: 'setup-fee', price }                         -> one-time order setup fee
//
// The four original const names are kept for compatibility (imported by testPage.tsx); only their values and
// the panel labels changed to match the setup-page names. Add entries keyed by the DiscoveryModePanel item id.

// "Price by element count" → flat fee bracketed by how many elements the design has (AmountOfElements).
export const PER_CUSTOMIZATION_PRICING_RULE = {
    kind: 'per-element',
    tiers: [
        { max: 3, price: 5 }, // 1–3 elements: $5
        { max: null, price: 10 } // 4+ elements: $10
    ]
};

// "Price per design side" → flat fee bracketed by the number of customized sides (CustomizedSides).
export const PER_PRINT_AREA_PRICING_RULE = {
    kind: 'per-side',
    tiers: [
        { max: 1, price: 5 }, // 1 side: $5
        { max: null, price: 10 } // 2+ sides: $10
    ]
};

// "Color surcharge" → flat fee bracketed by distinct colors used (AmountOfUsedColors); first 2 are free.
export const PER_COLOR_PRICING_RULE = {
    kind: 'per-color',
    tiers: [
        { max: 2, price: 0 }, // up to 2 colors: free
        { max: null, price: 5 } // 3+ colors: +$5
    ]
};

// "Quantity discount" → per-element customization fee that drops as the ORDER QUANTITY grows (volume
// discount). Unlike the other rules this reacts to the quantity selector, not to design edits. The customizer
// picks the tier with the highest minQuantity <= current qty, then charges `price * elementCount`.
export const QUANTITY_TIERS_PRICING_RULE = {
    kind: 'quantity-tiers',
    tiers: [
        { minQuantity: 1, price: 10 }, // 1–9 units: $10 / element
        { minQuantity: 10, price: 8 }, // 10–49 units: $8 / element
        { minQuantity: 50, price: 5 } // 50+ units: $5 / element
    ]
};

// "Price per text element" → fixed fee per text element added (PerElement, ALL_TEXTS).
export const PRICE_PER_TEXT_ELEMENT_PRICING_RULE = { kind: 'per-text-element', price: 5 };

// "Price per image upload" → fixed fee per image element added (PerElement, ALL_IMAGES).
export const PRICE_PER_IMAGE_UPLOAD_PRICING_RULE = { kind: 'per-image-upload', price: 10 };

// "Price by element size" → per-element fee bracketed by each element's printed area in cm² (ElementSize).
export const PRICE_BY_ELEMENT_SIZE_PRICING_RULE = {
    kind: 'element-size',
    tiers: [
        { max: 50, price: 5 }, // each element ≤ 50 cm²: $5
        { max: null, price: 10 } // each element > 50 cm²: $10
    ]
};

// "Price by canvas size" → incremental fee: one charge per unit of total print-area size (CanvasSize).
export const PRICE_BY_CANVAS_SIZE_PRICING_RULE = { kind: 'canvas-size', unitCm2: 10, pricePerUnit: 2 };

// "Price by design size" → flat fee bracketed by the total customized area across the design (DesignSize).
export const PRICE_BY_DESIGN_SIZE_PRICING_RULE = {
    kind: 'design-size',
    tiers: [
        { max: 50, price: 3 }, // total design ≤ 50 cm²: $3
        { max: null, price: 8 } // total design > 50 cm²: $8
    ]
};

// "Setup fee" → one-time fee applied to the order whenever the product is customized (AmountOfElements,
// isSetupPrice). The customizer amortizes it per unit so the order total stays a single flat fee.
export const SETUP_FEE_PRICING_RULE = { kind: 'setup-fee', price: 15 };

/**
 * Maps a DiscoveryModePanel pricing-rule button id to the payload streamed to the customizer. Buttons
 * without an entry here send `null` (no override) until they're implemented.
 */
export const DISCOVERY_PRICING_PAYLOADS: Record<string, object> = {
    'per-customization': PER_CUSTOMIZATION_PRICING_RULE,
    'per-print-area': PER_PRINT_AREA_PRICING_RULE,
    'per-color': PER_COLOR_PRICING_RULE,
    'quantity-tiers': QUANTITY_TIERS_PRICING_RULE,
    'price-per-text-element': PRICE_PER_TEXT_ELEMENT_PRICING_RULE,
    'price-per-image-upload': PRICE_PER_IMAGE_UPLOAD_PRICING_RULE,
    'price-by-element-size': PRICE_BY_ELEMENT_SIZE_PRICING_RULE,
    'price-by-canvas-size': PRICE_BY_CANVAS_SIZE_PRICING_RULE,
    'price-by-design-size': PRICE_BY_DESIGN_SIZE_PRICING_RULE,
    'setup-fee': SETUP_FEE_PRICING_RULE
};
